import { Component, h, Host, Prop, Watch, State } from '@stencil/core';
import { Element, Event, EventEmitter } from '@stencil/core';

interface OptionsInterface {
  value: string;
  label: string;
}

@Component({
  tag: 'signet-select-alt',
  styleUrl: 'signet-select-alt.scss',
  shadow: false,
})
export class SignetSelectAlt {
  @Element() el!: HTMLElement;

  @Event({ eventName: 'signetChange' }) signetChange: EventEmitter;
  @Prop() label: string;
  @Prop() placeholder: string;
  @Prop() isSearchable: boolean;
  @Prop() isClearable: boolean;
  @Prop() disabled: boolean;
  @Prop() isMulti = false;
  @Prop() options: OptionsInterface[] | string;
  @Prop() value: string | string[] = this.isMulti ? [] : '';

  @State() inputValue: string;
  @State() _options: OptionsInterface[];
  @State() openOptions: boolean;
  @State() selectedAnOption: boolean;
  @State() filteredOptions: OptionsInterface[];

  @Watch('options')
  parseOptionsProp(newValue: OptionsInterface[] | string) {
    if (typeof newValue === 'string') {
      this._options = JSON.parse(newValue);
    } else {
      this._options = newValue;
    }

    this.filteredOptions = [...this._options];
  }

  componentWillLoad() {
    this.parseOptionsProp(this.options);
  }

  onInputChange(e) {
    const value = e.target.value.toLowerCase();
    this.inputValue = value;

    if (value) {
      this.filteredOptions = this._options.filter((item) =>
        item.label.toLowerCase().includes(value)
      );
    } else {
      this.filteredOptions = [...this._options];
    }

    this.selectedAnOption = false;
  }

  handleOptionClick(option: OptionsInterface) {
    if (this.isMulti) {
      const selectedValues = this.value as string[];
      if (selectedValues.includes(option.value)) {
        this.value = selectedValues.filter((val) => val !== option.value);
      } else {
        this.value = [...selectedValues, option.value];
      }
      this.inputValue = '';
    } else {
      this.inputValue = option.label;
      this.value = option.value;
    }
    this.selectedAnOption = true;
    this.filteredOptions = [...this._options];

    this.toggleOpenOption();

    this.signetChange.emit(this.value);

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.value
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.value
    }));
  }

  toggleOpenOption() {
    if (this.disabled) {
      return;
    }

    this.openOptions = !this.openOptions;
  }

  private handleDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.select-alt-container')) {
      this.openOptions = false;
      this.filteredOptions = [...this._options];

      if (!this.selectedAnOption) {
        this.inputValue = '';
      }
    }
  }

  clearField() {
    this.value = this.isMulti ? [] : '';
    this.inputValue = '';
  }

  connectedCallback() {
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick.bind(this));
  }

  render() {
    const selectClasses = {
      'select-alt-container': true,
      disabled: this.disabled,
    };

    return (
      <Host>
        <label class="select-alt-label" htmlFor="select-alt">
          {this.label}
        </label>
        <div
          class={selectClasses}
          style={{
            border: `1px solid ${
              this.openOptions ? 'var(--primary-main)' : 'var(--gray-900)'
            }`,
            borderRadius: '4px',
          }}
        >
          <div class="input-container">
            {this.isMulti && (this.value as string[]).length > 0 ? (
              <div class="multi-container">
                {(this.value as string[]).map((val) => {
                  const option = this._options.find((opt) => opt.value === val);
                  return option ? (
                    <div class="multi-value">
                      <span class="multi-value-label">{option.label}</span>
                      <signet-icon
                        icon="close"
                        class="multi-value-close"
                        onClick={() => this.handleOptionClick(option)}
                      ></signet-icon>
                    </div>
                  ) : null;
                })}
              </div>
            ) : (
              ''
            )}

            <input
              class="select-input"
              name="select-alt"
              id="select-alt"
              type="text"
              value={this.inputValue}
              placeholder={this.value.length ? '' : this.placeholder}
              onInput={(evt) => this.onInputChange(evt)}
              readOnly={!this.isSearchable}
              disabled={this.disabled}
              onClick={() => this.toggleOpenOption()}
            />
          </div>

          {this.openOptions && (
            <div class="select-alt-options">
              {this.filteredOptions.map((item, index) => (
                <div
                  key={index}
                  class="option"
                  onClick={() => this.handleOptionClick(item)}
                >
                  {item.label}
                </div>
              ))}
            </div>
          )}

          {this.value?.length && this.isClearable ? (
            <signet-icon
              icon="close"
              class="select-alt-close-icon"
              onClick={() => this.clearField()}
            ></signet-icon>
          ) : (
            ''
          )}

          <signet-icon
            icon="keyboard_arrow_down"
            class="select-alt-icon"
          ></signet-icon>
        </div>
      </Host>
    );
  }
}
